import _ from "underscore";
import analytics from "web/script/analytics/analytics";
import baustein from "baustein";
import events from "web/script/dom/events";
import browser from "web/script/utils/browser";
import globals from "web/script/modules/globals";
import { gettext } from "web/script/modules/django-i18n";


const name = "app-header-menu";

export const selectors = {
    GENDER_TITLE: `.${name}__gender-title`,
    GENDERS_WRAPPER: `.${name}__genders`,
    GENDER: `.${name}__gender`,
    EXIT_CTA: `.${name}__exit-cta`,
    BACK_CTA: `.${name}__back-cta`,
    CELL: `.${name}__cell`,
    GENDER_NAV_CELL: `.${name}__cell[data-target-category]`,
    CELL_ACTIVE: `.${name}__cell--active`,
    GENDER_SECTION: `.${name}__gender__section`,
    GENDER_CATEGORIES: `.${name}__gender__section--categories`,
    GENDER_CATEGORY_ACTIVE: `.${name}__gender__section__category--active`,
    SPINNER: `.${name}__spinner`,
    HEADER: ".app-header",
    HEADER_BAR: ".app-header-bar",
};

const states = {
    ACTIVE: `${name}--active`,
    GENDER_NAV: `${name}__gender__section--categories-nav`,
    GENDER_SECTION_ACTIVE: `${name}__gender__section--active`,
    GENDER_CATEGORY_ACTIVE: `${name}__gender__section__category--active`,
    CELL_ACTIVE: `${name}__cell--active`,
};

export const WIDE_MENU_BREAKPOINT = 768;

export default baustein.register(name, {
    init() {
        /**
         * To communicate with external components whether the menu is currently open or not.
         * @see 'components/app-header'
         * @type {boolean}
         */
        this.isOpen = false;

        /**
         * The default category to toggle when the user opens the menu.
         * @type {string}
         */
        this._defaultCategory = "clothing";

        /**
         * The time the menu takes to animate, either via transform (narrow screen) or
         * opacity (wider screens).
         * @type {number}
         */
        this.animationTime = 150;

        /**
         * The active gender for the menu, in case the menu is open.
         * @type {string | null}
         */
        this.activeGender = null;
    },

    setupEvents(add) {
        // Navigation
        add("click", selectors.EXIT_CTA, this._exit);
        add("click", selectors.BACK_CTA, this._toCategoriesNav);

        if (this.isNarrowMenu()) {
            add("click", selectors.GENDER_NAV_CELL, this._toCategory);
        } else {
            add("mouseenter", selectors.GENDER_NAV_CELL, this._toCategory);
            add("click", selectors.GENDER_NAV_CELL, this._toCategory);
        }

        this.setGlobalHandler("keydown", this._exit);
    },

    /**
     * Open or close the menu, this will be usually triggered by 'components/app-header'.
     * Tries to set the Gender CTA state accordingly.
     *
     * On a wider viewport:
     * • It'll display the first category in the menu by default when opening.
     * • It'll switch genders (without open/close) if the menu is open and `switch` truthy.
     *
     * @see 'components/app-header'
     * @param {string | null} gender A string representing the selected gender.
     * @param {boolean} switchGender Whether the user is switching gender or not.
     * @returns {undefined}
     */
    toggle(gender, switchGender) {
        const shouldDisplayDefaultCategory = !this.isNarrowMenu() && !this.isOpen;
        const headerEl = this.el.querySelector(selectors.GENDER_TITLE);
        const spinnerEl = this.el.querySelector(selectors.SPINNER);
        const gendersWrapperEl = this.el.querySelector(selectors.GENDERS_WRAPPER);
        const analyticsGender = gender || this.activeGender;

        this.activeGender = (!this.isOpen && gender) || switchGender ? gender : null;

        if (gender && this.isNarrowMenu()) {
            if (gender === "men") {
                headerEl.textContent = gettext("general.men");
            } else if (gender === "women") {
                headerEl.textContent = gettext("general.women");
            }
        }

        if (shouldDisplayDefaultCategory || switchGender) {
            requestAnimationFrame(() => {
                if (switchGender) {
                    gendersWrapperEl.classList.add("vishidden");
                    spinnerEl.classList.remove("hidden");
                }

                this._handleActiveCategory();
                this._toggleCategory();

                if (switchGender) {
                    window.setTimeout(() => {
                        spinnerEl.classList.add("hidden");
                        gendersWrapperEl.classList.remove("vishidden");
                    }, this.animationTime * 1.5);
                }
            });
        }

        if (this.isNarrowMenu()) {
            this._toCategoriesNav();
        }

        if (!switchGender) {
            this.el.classList.toggle(states.ACTIVE);
            this.isOpen = !this.isOpen;
            this.emit(this.isOpen ? "appHeaderMenuOpened" : "appHeaderMenuClosed");
        }

        analytics.event(
            "nav",
            `${this.isOpen ? "opened" : "closed"}`,
            `nav_${analyticsGender}_menu`
        );
    },

    /**
     * Callback wrapper around `toggle`, to leave `toggle` event agnostic.
     * Handles both click and keydown events. Only responds to the keydown event if the
     * pressed key is the ESC key.
     *
     * @see toggle
     * @param {Object} event Baustein's wrapper around DOM native event object.
     * @returns {undefined}
     */
    _exit(event) {
        const key = event.key || event.keyCode || null;

        if ((key && key !== "Escape") || !this.isOpen) {
            return;
        }

        requestAnimationFrame(() => {
            this.toggle(this.activeGender);
        });
    },

    /**
     * Callback wrapper around `_toggleCategory`, to leave `_toggleCategory` event agnostic.
     *
     * @see _toggleCategory
     * @param {Object} event Baustein's wrapper around DOM native event object.
     * @param {HTMLElement} target A reference to the target property of the native DOM event object.
     * @returns {undefined}
     */
    _toCategory(event, target) {
        requestAnimationFrame(() => {
            let sendAnalytics = true;

            if (!this.isNarrowMenu()) {
                this._handleActiveCategory(target);
                sendAnalytics = false;
            }

            this._toggleCategory(target, sendAnalytics);
        });
    },

    /**
     * Method in charge to handle main navigation between menu categories.
     * It'll try to read a category ID from a supplied target and display a matching category
     * container. If no target is supplied, it'll try to display a default category.
     *
     * @param {HTMLElement, optional} target The target from which to read which category to navigate to.
     * @param {boolean, optional} sendAnalytics Only true if this is a user-triggered event and we need to send analytics
     * @returns {undefined}
     */
    _toggleCategory(target, sendAnalytics = false) {
        const activeGender = this._getActiveGenderEl();
        const genderSections = activeGender.querySelectorAll(selectors.GENDER_SECTION);
        const genderCategoriesSection = activeGender.querySelector(selectors.GENDER_CATEGORIES);
        const backEl = this.el.querySelector(selectors.BACK_CTA);
        const targetCategory = _.isElement(target)
            ? target.dataset.targetCategory
            : this._defaultCategory;

        [...genderSections].forEach((genderSection) => {
            // If we're on the Categories section, try to display the associated active category data,
            // else just toggle the active state for the current section.
            if (genderSection === genderCategoriesSection) {
                const activeEl = genderCategoriesSection.querySelector(
                    selectors.GENDER_CATEGORY_ACTIVE
                );
                const targetEl = genderCategoriesSection.querySelector(
                    `[data-category='${targetCategory}']`
                );

                // For the wide version of the menu, this is the only bit of logic that it matters,
                // the rest of CSS class toggling logic has to do with the narrow menu.
                [activeEl, targetEl].forEach((el) => {
                    if (el) {
                        el.classList.toggle(states.GENDER_CATEGORY_ACTIVE);
                    }
                });

                if (sendAnalytics) {
                    analytics.event("nav", "clicked", `user_click_${targetCategory}`);
                }

                this._listenTransition(genderSection, () =>
                    genderSection.classList.toggle(states.GENDER_SECTION_ACTIVE)
                ).then(() => backEl.classList.remove("hidden"));
            } else {
                genderSection.classList.toggle(states.GENDER_SECTION_ACTIVE);
            }
        });
    },

    /**
     * Navigate to the main category listing, hiding `__back-cta` (it's only displayed on mobile,
     * on the category detail page).
     *
     * @param {Object} event Baustein's wrapper around native DOM event object.
     * @param {HTMLElement} target A reference to the target property of the native DOM event object.
     * @returns {undefined}
     */
    _toCategoriesNav(event, target) {
        const activeGender = this._getActiveGenderEl();
        const screenEls = activeGender.querySelectorAll(selectors.GENDER_SECTION);
        const activeScreenEl = activeGender.querySelector(`.${states.GENDER_SECTION_ACTIVE}`);

        if (activeScreenEl.classList.contains(states.GENDER_NAV)) {
            return;
        }

        target = _.isElement(target) ? target : this.el.querySelector(selectors.BACK_CTA);

        [...screenEls].forEach((screen) => {
            screen.classList.toggle(states.GENDER_SECTION_ACTIVE);
            target.classList.add("hidden");
        });
    },

    /**
     * Handles active category highlighting on menu for wider viewports.
     *
     * @param {HTMLElement} targetEl
     * @returns {undefined}
     */
    _handleActiveCategory(targetEl) {
        const activeGenderEl = this._getActiveGenderEl();
        const activeCellEl = activeGenderEl.querySelector(selectors.CELL_ACTIVE);

        if (activeCellEl) {
            activeCellEl.classList.remove(states.CELL_ACTIVE);
        }

        if (targetEl && _.isElement(targetEl)) {
            targetEl.classList.add(states.CELL_ACTIVE);
        } else {
            activeGenderEl
                .querySelector(selectors.GENDER_NAV_CELL)
                .classList.add(states.CELL_ACTIVE);
        }
    },

    /**
     * Small utility to know if we're dealing with the wide or narrow version of the menu.
     *
     * @public
     * @returns {boolean}
     */
    isNarrowMenu() {
        return browser.getWindowWidth() < WIDE_MENU_BREAKPOINT;
    },

    /**
     * A friendly wrapper around `events.onTransitionEnd` with error handling. Returns a promise
     * that resolves once the transition is done.
     *
     * @param {HTMLElement} el The element on which to listen the transition animation.
     * @param {Function} action The action (function) that will cause the transition to happen.
     * @returns {Promise}
     */
    _listenTransition(el, action) {
        if (!el || !action) {
            logNoArgsError();
            return;
        }

        return new Promise((resolve, reject) => {
            events
                .onTransitionEnd(el, action, 200, "transform")
                .then((el) => {
                    resolve(el);
                })
                .catch(() => {
                    console.error(logTransitionError.bind(null, el));
                    reject(el);
                });
        });

        function logNoArgsError() {
            console.error(`Either an el "${el}" or an action "${action}" weren't supplied.`);
        }

        function logTransitionError(el) {
            console.error(`Listening for transition in ${el} failed.`);
        }
    },

    /**
     * Compute by how much offset (regarding `.app-header`) should the menu element be positioned
     * in the bottom direction.
     * Consumed by toggle().
     *
     * @see toggle()
     * @returns {string}
     */
    _computeMenuOffset() {
        const headerBar = globals.document.querySelector(selectors.HEADER_BAR);
        const headerBarElRect = headerBar.getBoundingClientRect();
        let offset = this._parseHeaderOffset();

        if (!this.isNarrowMenu()) {
            offset += headerBarElRect.top + headerBarElRect.height;
        }

        return `${offset}px`;
    },

    /**
     * When the users scroll, `.app-header` (which is fixed) gets translated on the Y axis to
     * hide `.app-pre-header-bar` and save screen size for the displayed page content. This method
     * is in charge to compute that translation offset on the Y axis.
     *
     * @see _computeMenuOffset(), components/app-header _onScroll()
     * @returns {number}
     */
    _parseHeaderOffset() {
        const translatedYOffset = globals.document.querySelector(selectors.HEADER).style.transform;

        return !translatedYOffset ? 0 : parseInt(translatedYOffset.match(/\d+/)[0], 10);
    },

    /**
     * Gets the current active Gender element containing menu data either for womens or mens.
     * Hides the non-active Gender element.
     *
     * @returns {HTMLElement | null}
     */
    _getActiveGenderEl() {
        const genderEl = this.activeGender
            ? this.el.querySelector(`${selectors.GENDER}--${this.activeGender}`)
            : this.el.querySelector(selectors.GENDER);

        Array.from(this.el.querySelectorAll(selectors.GENDER)).forEach((gender) =>
            gender.classList[gender === genderEl ? "remove" : "add"]("hidden")
        );

        return genderEl;
    },
});
